import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { Approach, ApproachVersion } from 'types/approach';
import { MarketplaceMetrics } from 'types/marketplace';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import MetricsBadgeAtom from '../../atoms/marketplace/MetricsBadgeAtom';
import UseCaseTagPillsAtom from '../../atoms/marketplace/UseCaseTagPillsAtom';
import ItemTypeGlyphAtom from '../../atoms/marketplace/ItemTypeGlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ApproachHeaderMolecule.scss';

export interface ApproachHeaderMoleculeProps {
  approach: Approach;
  version?: ApproachVersion | null;
  metrics?: MarketplaceMetrics | null;
  onDeploy?: () => void;
  isDeploying?: boolean;
  onTagClick?: (tag: string) => void;
  publisherName?: string;
  isSystemPublisher?: boolean;
  /** When provided, the publisher name renders as a button. */
  onPublisherClick?: () => void;
  /** Channel name to render in the meta row as "in {channel}". */
  channelName?: string;
  /** When provided alongside `channelName`, renders the channel name as a button. */
  onChannelClick?: () => void;
  deploymentMode?: 'once' | 'many';
  /** Shown next to Deploy when the current user can manage the published channel_item. */
  onManage?: () => void;
  /** Renders a small "← Back to channel" link below the action buttons. */
  onBackToChannel?: () => void;
  className?: string;
}

const ApproachHeaderMolecule: React.FC<ApproachHeaderMoleculeProps> = ({
  approach,
  version,
  metrics,
  onDeploy,
  isDeploying = false,
  onTagClick,
  publisherName,
  isSystemPublisher = false,
  onPublisherClick,
  channelName,
  onChannelClick,
  deploymentMode,
  onManage,
  onBackToChannel,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-approach-header-molecule', className]
    .filter(Boolean)
    .join(' ');

  const displayName = getLocalizedText(approach.name) || approach.system_name;
  const displayDescription = getLocalizedText(approach.description);
  const isDraft = version?.is_draft ?? false;
  const publishedDate = version?.published_at
    ? new Date(version.published_at).toLocaleDateString()
    : null;

  const ratingAvg = metrics?.rating_average
    ? Number(metrics.rating_average)
    : 0;
  const ratingCount = metrics?.rating_count
    ? Number(metrics.rating_count)
    : 0;
  const importCount = metrics?.import_count
    ? Number(metrics.import_count)
    : 0;

  const useCaseTags = approach.use_case_tags ?? [];

  // When the approach is a sphere template (templateFor set), show the
  // target schema's glyph in the header so the icon reflects what the
  // approach actually does. Otherwise fall back to the clipboard glyph.
  const templateFor = version?.definition?.templateFor;
  const headerSchemaNames = useMemo(
    () => (templateFor ? [templateFor] : []),
    [templateFor],
  );
  const { resolved: headerSchemaMetadata } = useSchemaMetadata(headerSchemaNames);
  const headerSchemaMeta = templateFor
    ? headerSchemaMetadata.get(templateFor)
    : undefined;
  const headerSchemaGlyph = headerSchemaMeta?.glyph ?? null;
  const headerSchemaGlyphColor = headerSchemaMeta?.glyphColor;
  const headerSchemaLabel = headerSchemaMeta?.displayName;

  return (
    <div className={classes}>
      <div className="marketplace-approach-header-molecule__top">
        {headerSchemaGlyph && (
          <span
            className="marketplace-approach-header-molecule__icon"
            aria-hidden="true"
          >
            <SphereAvatar
              glyph={headerSchemaGlyph}
              glyphColor={headerSchemaGlyphColor}
              size="medium"
              shape="square"
              altText={headerSchemaLabel ?? displayName}
            />
          </span>
        )}
        {!headerSchemaGlyph && (
          <ItemTypeGlyphAtom
            type="approach"
            size={60}
            className="marketplace-approach-header-molecule__icon"
            ariaLabel={t('marketplace.approachDetail.glyphAriaLabel')}
          />
        )}

        <div className="marketplace-approach-header-molecule__title-area">
          <div className="marketplace-approach-header-molecule__name-row">
            <h1 className="marketplace-approach-header-molecule__name">
              {displayName}
            </h1>
            <TagAtom
              tone="info"
              icon="pi pi-compass"
              label={t('marketplace.common.itemType.approach')}
              size="small"
              className="marketplace-approach-header-molecule__type-chip"
            />
            {version && (
              <span className="marketplace-approach-header-molecule__version-badge">
                {t('marketplace.approachDetail.versions.versionLabel', {
                  version: version.version,
                })}
              </span>
            )}
            {isDraft && (
              <span className="marketplace-approach-header-molecule__draft-badge">
                {t('marketplace.approachDetail.draftBadge')}
              </span>
            )}
            {deploymentMode === 'once' && (
              <TagAtom
                tone="warn"
                icon="pi pi-lock"
                label={t('marketplace.approachDetail.deploymentMode.once')}
                size="small"
                className="marketplace-approach-header-molecule__deployment-mode-badge"
              />
            )}
          </div>

          <div className="marketplace-approach-header-molecule__meta">
            {publisherName && (
              <>
                {(() => {
                  if (isSystemPublisher) {
                    return (
                      <span className="marketplace-approach-header-molecule__system-badge">
                        {t('marketplace.approachDetail.publisher.centerPlatform')}
                      </span>
                    );
                  }
                  if (onPublisherClick) {
                    return (
                      <button
                        type="button"
                        className="marketplace-approach-header-molecule__publisher-link"
                        onClick={onPublisherClick}
                      >
                        {t('marketplace.approachDetail.byPublisher', {
                          publisher: publisherName,
                        })}
                      </button>
                    );
                  }
                  return (
                    <span className="marketplace-approach-header-molecule__publisher">
                      {t('marketplace.approachDetail.byPublisher', {
                        publisher: publisherName,
                      })}
                    </span>
                  );
                })()}
              </>
            )}
            {channelName && (
              <>
                {publisherName && (
                  <span className="marketplace-approach-header-molecule__separator">
                    &middot;
                  </span>
                )}
                {onChannelClick ? (
                  <button
                    type="button"
                    className="marketplace-approach-header-molecule__channel-link"
                    onClick={onChannelClick}
                  >
                    {t('marketplace.approachDetail.inChannel', {
                      channel: channelName,
                    })}
                  </button>
                ) : (
                  <span className="marketplace-approach-header-molecule__channel">
                    {t('marketplace.approachDetail.inChannel', {
                      channel: channelName,
                    })}
                  </span>
                )}
              </>
            )}
            {publishedDate && (
              <>
                {(publisherName || channelName) && (
                  <span className="marketplace-approach-header-molecule__separator">
                    &middot;
                  </span>
                )}
                <span className="marketplace-approach-header-molecule__published">
                  {t('marketplace.approachDetail.publishedDate', {
                    date: publishedDate,
                  })}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="marketplace-approach-header-molecule__actions">
          {onManage && (
            <button
              type="button"
              className="marketplace-approach-header-molecule__manage-btn"
              onClick={onManage}
              aria-label={t('marketplace.approachDetail.manageButton')}
            >
              <i className="pi pi-cog" aria-hidden="true" />
              <span>{t('marketplace.approachDetail.manageButton')}</span>
            </button>
          )}
          {onDeploy && (
            <button
              type="button"
              className="marketplace-approach-header-molecule__deploy-btn"
              disabled={isDeploying}
              onClick={onDeploy}
            >
              {isDeploying
                ? t('marketplace.approachDetail.deploying')
                : t('marketplace.approachDetail.deployButton')}
            </button>
          )}
          {onBackToChannel && (
            <button
              type="button"
              className="marketplace-approach-header-molecule__back-to-channel"
              onClick={onBackToChannel}
            >
              <i className="pi pi-arrow-left" aria-hidden="true" />
              <span>{t('marketplace.approachDetail.backToChannel')}</span>
            </button>
          )}
        </div>
      </div>

      <div className="marketplace-approach-header-molecule__metrics-row">
        {ratingAvg > 0 && (
          <div className="marketplace-approach-header-molecule__rating">
            <StarDisplayAtom rating={ratingAvg} size="normal" showValue />
            {ratingCount > 0 && (
              <span className="marketplace-approach-header-molecule__rating-count">
                {t('marketplace.approachDetail.ratingCount', {
                  count: ratingCount,
                })}
              </span>
            )}
          </div>
        )}
        <MetricsBadgeAtom
          importCount={importCount > 0 ? importCount : undefined}
          className="marketplace-approach-header-molecule__badge"
        />
      </div>

      {displayDescription && (
        <p className="marketplace-approach-header-molecule__description">
          {displayDescription}
        </p>
      )}

      {useCaseTags.length > 0 && (
        <UseCaseTagPillsAtom
          tags={useCaseTags}
          maxVisible={5}
          size="normal"
          onTagClick={onTagClick}
          className="marketplace-approach-header-molecule__tags"
        />
      )}
    </div>
  );
};

export default ApproachHeaderMolecule;
